import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Loader2, Building2, Users, ListChecks, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PROJECT_STATUSES, SYSTEM_ROLES, PROJECT_STATUS_COLOR, moneyExact } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const c = PROJECT_STATUS_COLOR[status] || "#94A3B8";
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wide"
      style={{ color: c, backgroundColor: `${c}22`, border: `1px solid ${c}55` }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: c }} />
      {status}
    </span>
  );
}

function NewProjectDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", start_date: "", end_date: "", budget: "", currency: "USD", status: "Planning",
  });
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("Site Worker");

  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data.users,
    enabled: open,
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addInvite = () => {
    if (!inviteEmail) return;
    if (invites.some((i) => i.email === inviteEmail)) return;
    setInvites((v) => [...v, { email: inviteEmail, role_tier: inviteRole }]);
    setInviteEmail("");
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        budget: parseFloat(form.budget || "0"),
        members: invites,
      };
      return (await api.post("/projects", payload)).data.project;
    },
    onSuccess: () => {
      toast.success("Project workspace created");
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["projects-nav"] });
      setOpen(false);
      setForm({ name: "", description: "", start_date: "", end_date: "", budget: "", currency: "USD", status: "Planning" });
      setInvites([]);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-project-button"
          className="h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-bold">
          <Plus className="h-5 w-5 mr-1" /> New Project
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black">Initialize Workspace</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-5">
          <div className="space-y-2">
            <Label className="label-caps">Project Name</Label>
            <Input required value={form.name} onChange={set("name")} data-testid="project-name-input"
              className="h-12 bg-background" placeholder="e.g. Riverside Tower Phase II" />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Description</Label>
            <Textarea value={form.description} onChange={set("description")} data-testid="project-description-input"
              className="bg-background min-h-20" placeholder="Scope, location, key deliverables…" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="label-caps">Start Date</Label>
              <Input type="date" required value={form.start_date} onChange={set("start_date")}
                data-testid="project-start-input" className="h-12 bg-background" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Target End</Label>
              <Input type="date" required value={form.end_date} onChange={set("end_date")}
                data-testid="project-end-input" className="h-12 bg-background" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2 col-span-2">
              <Label className="label-caps">Budget</Label>
              <Input type="number" min="0" step="0.01" required value={form.budget} onChange={set("budget")}
                data-testid="project-budget-input" className="h-12 bg-background font-data" placeholder="0.00" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Currency</Label>
              <Input value={form.currency} onChange={set("currency")} maxLength={3}
                data-testid="project-currency-input" className="h-12 bg-background font-data uppercase" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger className="h-12 bg-background" data-testid="project-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROJECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Member invites */}
          <div className="space-y-3 border-t border-border pt-4">
            <Label className="label-caps">Invite Members (Role Tier)</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={inviteEmail} onValueChange={setInviteEmail}>
                <SelectTrigger className="h-12 bg-background flex-1" data-testid="invite-email-select">
                  <SelectValue placeholder="Select user…" />
                </SelectTrigger>
                <SelectContent>
                  {(usersData || []).map((u) => (
                    <SelectItem key={u.id} value={u.email}>{u.first_name} {u.last_name} · {u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="h-12 bg-background sm:w-44" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SYSTEM_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="button" variant="secondary" onClick={addInvite} className="h-12" data-testid="add-invite-button">Add</Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {invites.map((i) => (
                <span key={i.email} className="inline-flex items-center gap-2 bg-background border border-border rounded px-2.5 py-1.5 text-sm">
                  {i.email} <span className="text-primary font-semibold">{i.role_tier}</span>
                  <button type="button" onClick={() => setInvites((v) => v.filter((x) => x.email !== i.email))}>
                    <X className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending} data-testid="create-project-submit"
              className="h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-bold w-full">
              {mutation.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create Workspace"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await api.get("/projects")).data.projects,
  });
  const projects = data || [];

  return (
    <div className="p-5 sm:p-8 max-w-[1600px] mx-auto fade-in">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-caps mb-2">Command Center</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Project Workspaces</h1>
          <p className="text-muted-foreground mt-1">{projects.length} active workspace{projects.length !== 1 ? "s" : ""}</p>
        </div>
        <NewProjectDialog />
      </div>

      {isLoading ? (
        <div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : projects.length === 0 ? (
        <div className="border border-border rounded-md bg-card p-12 text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-display text-xl font-bold">No workspaces yet</h3>
          <p className="text-muted-foreground mt-1">Create your first project to start managing tasks, blueprints and teams.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {projects.map((p) => {
            const pct = p.task_count ? Math.round((p.tasks_done / p.task_count) * 100) : 0;
            return (
              <button key={p.id} onClick={() => navigate(`/app/projects/${p.id}`)}
                data-testid={`project-card-${p.id}`}
                className="text-left bg-card border border-border rounded-md p-5 hover:border-primary transition-colors group">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <h3 className="font-display text-lg font-bold leading-tight group-hover:text-primary transition-colors">{p.name}</h3>
                  <StatusBadge status={p.status} />
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 min-h-10 mb-4">{p.description || "No description provided."}</p>

                <div className="grid grid-cols-2 gap-px bg-border border border-border rounded overflow-hidden mb-4">
                  <div className="bg-card p-3">
                    <div className="label-caps">Budget</div>
                    <div className="font-data font-bold text-lg mt-1">{moneyExact(p.budget, p.currency)}</div>
                  </div>
                  <div className="bg-card p-3">
                    <div className="label-caps">Actual Cost</div>
                    <div className="font-data font-bold text-lg mt-1 text-primary">{moneyExact(p.actual_cost_total, p.currency)}</div>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="label-caps">Progress</span>
                    <span className="font-data font-bold">{pct}%</span>
                  </div>
                  <div className="h-2 bg-background rounded overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>

                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><ListChecks className="h-4 w-4" /> {p.tasks_done}/{p.task_count} tasks</span>
                  <span className="flex items-center gap-1.5"><Users className="h-4 w-4" /> {p.member_count}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
